import { PasseriniAPI } from './apiService';
import { ApiResponse } from '@/types';

export interface Permission {
  id: number;
  name: string;
  display_name?: string;
  description?: string;
  category?: string;
  guard_name?: string;
  roles?: Role[];
  created_at: string;
  updated_at: string;
  pivot?: {
    role_id: number;
    permission_id: number;
  };
}

export interface Role {
  id: number;
  name: string;
  display_name?: string;
  description?: string;
  color?: string;
  sort_order?: number;
  guard_name?: string;
  permissions?: Permission[];
  is_protected?: boolean;
  users_count?: number;
  created_at: string;
  updated_at: string;
}

export interface CreatePermissionData {
  name: string;
  display_name?: string;
  description?: string;
  category?: string;
}

export interface UpdatePermissionData {
  name?: string;
  display_name?: string;
  description?: string;
  category?: string;
}

export interface CreateRoleData {
  name: string;
  display_name?: string;
  description?: string;
  color?: string;
  sort_order?: number;
}

export interface UpdateRoleData {
  name?: string;
  display_name?: string;
  description?: string;
  color?: string;
  sort_order?: number;
}

export interface AssignPermissionsData {
  permission_ids: number[];
}

export interface SyncPermissionsData {
  permissions: string[];
}

export const rolesPermissionsService = {
  // Permissions endpoints
  permissions: {
    getAll: async (): Promise<Permission[]> => {
      const response = await PasseriniAPI.get<ApiResponse<Permission[]>>('/permissions?with=roles');
      return response.data.data;
    },

    create: async (data: CreatePermissionData): Promise<Permission> => {
      const response = await PasseriniAPI.post<ApiResponse<Permission>>('/permissions', data);
      return response.data.data;
    },

    getById: async (id: number): Promise<Permission> => {
      const response = await PasseriniAPI.get<ApiResponse<Permission>>(`/permissions/${id}`);
      return response.data.data;
    },

    update: async (id: number, data: UpdatePermissionData): Promise<Permission> => {
      const response = await PasseriniAPI.put<ApiResponse<Permission>>(`/permissions/${id}`, data);
      return response.data.data;
    },

    delete: async (id: number): Promise<void> => {
      await PasseriniAPI.delete(`/permissions/${id}`);
    }
  },

  // Roles endpoints
  roles: {
    getAll: async (): Promise<Role[]> => {
      const response = await PasseriniAPI.get<ApiResponse<Role[]>>('/roles?with=permissions&include_protection_status=true');
      return response.data.data;
    },
    getAllFromNames: async (names: string[]): Promise<Role[]> => {
      const response = await PasseriniAPI.get<ApiResponse<Role[]>>(`/roles?with=permissions&include_protection_status=true&name[in]=${names.join(",")}`);
      return response.data.data;
    },

    create: async (data: CreateRoleData): Promise<Role> => {
      const response = await PasseriniAPI.post<ApiResponse<Role>>('/roles', data);
      return response.data.data;
    },

    getById: async (id: number): Promise<Role> => {
      const response = await PasseriniAPI.get<ApiResponse<Role>>(`/roles/${id}`);
      return response.data.data;
    },

    update: async (id: number, data: UpdateRoleData): Promise<Role> => {
      const response = await PasseriniAPI.put<ApiResponse<Role>>(`/roles/${id}`, data);
      return response.data.data;
    },

    delete: async (id: number): Promise<void> => {
      await PasseriniAPI.delete(`/roles/${id}`);
    },

    getProtectedRoles: async (): Promise<string[]> => {
      const response = await PasseriniAPI.get<ApiResponse<{ protected_roles: string[] }>>('/roles/protected/list');
      return response.data.data.protected_roles;
    },

    getUserCounts: async (): Promise<Role[]> => {
      const response = await PasseriniAPI.get<ApiResponse<{ roles: Role[] }>>('/roles/users/counts');
      return response.data.data.roles;
    }
  },

  // Role-Permission associations
  rolePermissions: {
    assign: async (roleId: number, data: AssignPermissionsData): Promise<void> => {
      await PasseriniAPI.post(`/roles/${roleId}/permissions/assign`, data);
    },

    remove: async (roleId: number, data: AssignPermissionsData): Promise<void> => {
      await PasseriniAPI.delete(`/roles/${roleId}/permissions/remove`, { data });
    },

    sync: async (roleId: number, data: SyncPermissionsData): Promise<void> => {
      await PasseriniAPI.put(`/roles/${roleId}/permissions/sync`, data);
    },

    getRolePermissions: async (roleId: number): Promise<Permission[]> => {
      const response = await PasseriniAPI.get<ApiResponse<Permission[]>>(`/roles/${roleId}/permissions`);
      return response.data.data;
    },

    getPermissionRoles: async (permissionId: number): Promise<Role[]> => {
      const response = await PasseriniAPI.get<ApiResponse<Role[]>>(`/permissions/${permissionId}/roles`);
      return response.data.data;
    },

    syncRolesToPermission: async (permissionId: number, roleNames: string[]): Promise<void> => {
      await PasseriniAPI.put(`/permissions/${permissionId}/roles/sync`, { roles: roleNames });
    }
  }
};